import { AlertTriangle, ChevronLeft, Home, RefreshCw } from 'lucide-react';
import { motion } from 'motion/react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardFooter, CardHeader } from '@/components/ui/card';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Separator } from '@/components/ui/separator';
import { useRouter } from '@/hooks/use-router';

export type ErrorWithStatus = Error & {
  status?: number;
  statusCode?: number;
  errors?: unknown;
};

export function ErrorPage({ error }: { error: ErrorWithStatus }) {
  const status = error.status || error.statusCode || 500;
  const router = useRouter();

  const hasJsonErrors =
    error.errors && typeof error.errors === 'object' && !Array.isArray(error.errors);

  const formattedErrors = hasJsonErrors ? JSON.stringify(error.errors, null, 2) : null;

  const handleReload = () => {
    router.refresh();
  };

  const handleBack = () => {
    router.back();
  };

  const handleHome = () => {
    router.push('/');
  };

  return (
    <div className="relative flex min-h-screen items-center justify-center overflow-hidden bg-background p-6">
      <motion.div
        animate={{
          scale: [1, 1.2, 1],
          opacity: [0.15, 0.25, 0.15],
        }}
        transition={{
          duration: 8,
          repeat: Infinity,
          ease: 'easeInOut',
        }}
        className="absolute inset-0 flex items-center justify-center"
      >
        <div className="h-[500px] w-[500px] rounded-full bg-destructive/20 blur-3xl" />
      </motion.div>

      <motion.div
        initial={{ opacity: 0, y: 30, scale: 0.96 }}
        animate={{ opacity: 1, y: 0, scale: 1 }}
        transition={{
          duration: 0.45,
          ease: 'easeOut',
        }}
        className="relative z-10 w-full max-w-4xl"
      >
        <Card className="overflow-hidden border-destructive/20 shadow-2xl backdrop-blur">
          <CardHeader className="relative overflow-hidden border-b bg-destructive/5">
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.2 }}
              className="absolute inset-0"
            >
              <div className="absolute inset-0 bg-[linear-gradient(to_right,hsl(var(--border))_1px,transparent_1px),linear-gradient(to_bottom,hsl(var(--border))_1px,transparent_1px)] bg-[size:40px_40px] opacity-[0.03]" />
            </motion.div>

            <div className="relative flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
              <div className="flex items-start gap-4">
                <motion.div
                  animate={{
                    rotate: [0, -8, 8, -4, 4, 0],
                  }}
                  transition={{
                    duration: 1.8,
                    repeat: Infinity,
                    repeatDelay: 3,
                  }}
                  className="flex size-14 items-center justify-center rounded-2xl bg-destructive/10 text-destructive shadow-lg"
                >
                  <AlertTriangle className="size-7" />
                </motion.div>

                <div className="space-y-1">
                  <motion.h1
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.1 }}
                    className="text-3xl font-bold tracking-tight"
                  >
                    Ocorreu um erro
                  </motion.h1>

                  <motion.p
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.18 }}
                    className="text-muted-foreground"
                  >
                    Algo inesperado aconteceu durante o carregamento da página.
                  </motion.p>
                </div>
              </div>

              <motion.div
                initial={{ opacity: 0, x: 15 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: 0.25 }}
                whileHover={{ scale: 1.04 }}
                className="flex items-center gap-2 rounded-xl border bg-background/80 px-4 py-3 shadow-sm backdrop-blur"
              >
                <span className="text-sm text-muted-foreground">Status</span>

                <Separator orientation="vertical" className="h-5" />

                <motion.span
                  key={status}
                  initial={{ scale: 0.7, opacity: 0 }}
                  animate={{ scale: 1, opacity: 1 }}
                  transition={{
                    type: 'spring',
                    stiffness: 300,
                    damping: 18,
                  }}
                  className="text-2xl font-bold text-destructive"
                >
                  {status}
                </motion.span>
              </motion.div>
            </div>
          </CardHeader>

          <CardContent className="space-y-6 p-6">
            <motion.div
              initial={{ opacity: 0, y: 14 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.3 }}
              className="space-y-2"
            >
              <h2 className="text-sm font-semibold uppercase tracking-wide text-muted-foreground">
                Mensagem
              </h2>

              <motion.div
                whileHover={{ scale: 1.01 }}
                className="rounded-xl border bg-muted/40 p-4 transition-colors"
              >
                <p className="break-words font-medium">
                  {error.message || 'Erro desconhecido'}
                </p>
              </motion.div>
            </motion.div>

            {formattedErrors ? (
              <motion.div
                initial={{ opacity: 0, y: 14 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.38 }}
                className="space-y-2"
              >
                <h2 className="text-sm font-semibold uppercase tracking-wide text-muted-foreground">
                  Detalhes do erro
                </h2>

                <motion.div
                  whileHover={{ scale: 1.005 }}
                  className="overflow-hidden rounded-xl border"
                >
                  <ScrollArea className="h-80 bg-black">
                    <pre className="p-4 text-sm leading-6 text-orange-300">
                      <code>{formattedErrors}</code>
                    </pre>
                  </ScrollArea>
                </motion.div>
              </motion.div>
            ) : error.stack ? (
              <motion.div
                initial={{ opacity: 0, y: 14 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.38 }}
                className="space-y-2"
              >
                <h2 className="text-sm font-semibold uppercase tracking-wide text-muted-foreground">
                  Stack Trace
                </h2>

                <motion.div
                  whileHover={{ scale: 1.005 }}
                  className="overflow-hidden rounded-xl border"
                >
                  <ScrollArea className="h-80 bg-black">
                    <pre className="p-4 text-sm leading-6 text-green-400">
                      <code>{error.stack}</code>
                    </pre>
                  </ScrollArea>
                </motion.div>
              </motion.div>
            ) : null}
          </CardContent>

          <CardFooter className="flex flex-col gap-3 border-t bg-muted/20 p-6 sm:flex-row sm:justify-between">
            <motion.div
              whileHover={{ scale: 1.03 }}
              whileTap={{ scale: 0.96 }}
              className="w-full sm:w-auto"
            >
              <Button variant="outline" onClick={handleBack} className="w-full sm:w-auto">
                <ChevronLeft className="mr-2 size-4" />
                Voltar
              </Button>
            </motion.div>

            <div className="flex w-full flex-col gap-3 sm:w-auto sm:flex-row">
              <motion.div
                whileHover={{ scale: 1.03 }}
                whileTap={{ scale: 0.96 }}
                className="w-full sm:w-auto"
              >
                <Button
                  variant="secondary"
                  onClick={handleReload}
                  className="w-full sm:w-auto"
                >
                  <motion.div whileHover={{ rotate: 180 }} transition={{ duration: 0.4 }}>
                    <RefreshCw className="mr-2 size-4" />
                  </motion.div>
                  Recarregar
                </Button>
              </motion.div>

              <motion.div
                whileHover={{ scale: 1.03 }}
                whileTap={{ scale: 0.96 }}
                className="w-full sm:w-auto"
              >
                <Button onClick={handleHome} className="w-full sm:w-auto">
                  <Home className="mr-2 size-4" />
                  Ir para início
                </Button>
              </motion.div>
            </div>
          </CardFooter>
        </Card>
      </motion.div>
    </div>
  );
}
